import { EyeOutlined } from '@ant-design/icons';
import { BaseCard } from '@app/components/common/BaseCard/BaseCard';
import { BaseTable } from '@app/components/common/BaseTable/BaseTable';
import { useMounted } from '@app/hooks/useMounted';
import { Space, Tooltip, Button } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import React, { useCallback, useEffect, useState, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { TablePaginationConfig } from 'antd';
import FilterComponent from '@app/components/TableFilter/TableFilter';
import { getInvoiceListTableData } from '@app/api/finances.api';
import { InvoiceTableRow, Pagination } from '@app/models/finances/FinancesResponse';
import dayjs from 'dayjs';

const initialPagination: Pagination = {
  current: 1,
  pageSize: 5,
};

export type SelectOption = { label: string; value: string };

export const InvoicesTable: React.FC = () => {
  const [tableData, setTableData] = useState<{
    data: InvoiceTableRow[];
    pagination: Pagination;
    loading: boolean;
  }>({
    data: [],
    pagination: initialPagination,
    loading: false,
  });
  const { isMounted } = useMounted();
  const navigate = useNavigate();
  const [filters, setFilters] = useState<{ [key: string]: string | null }>({ status: 'PENDING' });
  const [statusOptions, setStatusOptions] = useState<Record<string, SelectOption[]> | null>(null);
  const [departmentOptions, setDepartmentOptions] = useState<Record<string, SelectOption[]> | null>(null);
  const [taskTypeOptions, setTypeTypeOptions] = useState<Record<string, SelectOption[]> | null>(null);
  const [customerTypeOptions, setCustomerTypeOptions] = useState<Record<string, SelectOption[]> | null>(null);
  const [cashierOptions, setCashierOptions] = useState<Record<string, SelectOption[]> | null>(null);
  const [cashierLocationOptions, setCashierLocaOptions] = useState<Record<string, SelectOption[]> | null>(null);
  const [dateRelatedOptions, setDateRelatedOptions] = useState<Record<string, SelectOption[]> | null>(null);
  const [paymentMethodOptions, setPaymentMethodOptions] = useState<Record<string, SelectOption[]> | null>(null);
  const [initialFiltersLoaded, setInitialFiltersLoaded] = useState(false);
  const [showDateRange, setShowDateRange] = useState(false);

  const normalize = (arr?: { label: string; value: string | number }[]) => {
    if (!Array.isArray(arr)) return [];
    return arr.map((i) => ({
      label: i.label,
      value: String(i.value),
    }));
  };

  // Single consolidated effect for both filter options and initial data fetch
  useEffect(() => {
    let isMountedLocal = true;

    const loadData = async () => {
      try {
        const res = await getInvoiceListTableData({});
        if (!isMountedLocal) return;

        // Load filter options
        setStatusOptions({ status: normalize(res.filter_options.status_options) });
        setDepartmentOptions({ department_id: normalize(res.filter_options.department_options) });
        setTypeTypeOptions({ referenceable_type: normalize(res.filter_options.referenceable_type_options) });
        setCustomerTypeOptions({ partner_type: normalize(res.filter_options.partner_type_options) });
        setCashierOptions({ cashier_id: normalize(res.filter_options.cashier_options) });
        setCashierLocaOptions({ cashier_location_id: normalize(res.filter_options.cashier_location_options) });
        setDateRelatedOptions({ date_related_to: normalize(res.filter_options.date_related_to_options) });
        setPaymentMethodOptions({ date_related_to: normalize(res.filter_options.payment_method_options) });

        // Set initial table data
        setTableData({
          data: res.data,
          pagination: {
            current: res.pagination.current,
            pageSize: res.pagination.pageSize,
            total: res.pagination.total,
          },
          loading: false,
        });
        setInitialFiltersLoaded(true);
      } catch (error) {
        console.error(error);
        if (isMountedLocal) {
          setTableData({ data: [], pagination: initialPagination, loading: false });
        }
      }
    };

    loadData();

    return () => {
      isMountedLocal = false;
    };
  }, []);

  // Single effect for refetching on filters or pagination change
  useEffect(() => {
    setTableData((prev) => ({ ...prev, loading: true }));

    const requestFilters = { ...initialPagination, ...filters } as any;
    // Ensure status is always PENDING if not explicitly set
    if (!requestFilters.status) {
      requestFilters.status = 'PENDING';
    }

    getInvoiceListTableData(requestFilters)
      .then((res) => {
        setTableData({
          data: res.data,
          pagination: {
            current: res.pagination.current,
            pageSize: res.pagination.pageSize,
            total: res.pagination.total,
          },
          loading: false,
        });
      })
      .catch((error: any) => {
        console.error(error);
        setTableData({ data: [], pagination: initialPagination, loading: false });
      });
  }, [filters]);

  const handleTableChange = (pagination: TablePaginationConfig) => {
    setTableData((prev) => ({ ...prev, loading: true }));
    const requestFilters = {
      current: pagination.current || 1,
      pageSize: pagination.pageSize || 5,
      ...filters,
    } as any;
    // Ensure status is always PENDING if not explicitly set
    if (!requestFilters.status) {
      requestFilters.status = 'PENDING';
    }

    getInvoiceListTableData(requestFilters)
      .then((res) => {
        setTableData({
          data: res.data,
          pagination: {
            current: res.pagination.current,
            pageSize: res.pagination.pageSize,
            total: res.pagination.total,
          },
          loading: false,
        });
      })
      .catch((error: any) => {
        console.error(error);
        setTableData({ data: [], pagination: initialPagination, loading: false });
      });
  };

  const showTotal = (total: number, range: [number, number]): React.ReactNode => {
    return (
      <span style={{ fontSize: '14px', marginRight: '20px', color: '#adadad' }}>
        {`Showing ${range[0]} - ${range[1]} of ${total} Invoices`}
      </span>
    );
  };

  const searchData: {
    name: string;
    placeholder: string;
    type: 'text' | 'dropdown' | 'daterange';
    options?: Record<string, SelectOption[]>;
    dependsOn?: string;
  }[] = useMemo(() => {
    const baseData: {
      name: string;
      placeholder: string;
      type: 'text' | 'dropdown' | 'daterange';
      options?: Record<string, SelectOption[]>;
      dependsOn?: string;
    }[] = [
      { name: 'invoice_number', placeholder: 'Invoice No', type: 'text' },
      { name: 'partner', placeholder: 'Customer', type: 'text' },
      {
        name: 'department_id',
        placeholder: 'Department',
        type: 'dropdown',
        options: departmentOptions ?? undefined,
      },
      { name: 'referenceable_type', placeholder: 'Task Type', type: 'dropdown', options: taskTypeOptions ?? undefined },
      { name: 'referenceable_code', placeholder: 'Task Code', type: 'text' },
      {
        name: 'partner_type',
        placeholder: 'Customer Type',
        type: 'dropdown',
        options: customerTypeOptions ?? undefined,
      },
      { name: 'cashier_id', placeholder: 'Cashier', type: 'dropdown', options: cashierOptions ?? undefined },
      {
        name: 'cashier_location_id',
        placeholder: 'Cashier Location',
        type: 'dropdown',
        options: cashierLocationOptions ?? undefined,
      },
      {
        name: 'payment_method',
        placeholder: 'Payment Method',
        type: 'dropdown',
        options: paymentMethodOptions ?? undefined,
      },
      { name: 'status', placeholder: 'Status', type: 'dropdown', options: statusOptions ?? undefined },
      {
        name: 'date_related_to',
        placeholder: 'Date Related to',
        type: 'dropdown',
        options: dateRelatedOptions ?? undefined,
      },
      {
        name: 'date_range',
        placeholder: 'Select Date Range',
        type: 'daterange',
        dependsOn: 'date_related_to',
      },
    ];
    return baseData;
  }, [
    departmentOptions,
    taskTypeOptions,
    customerTypeOptions,
    cashierOptions,
    cashierLocationOptions,
    statusOptions,
    dateRelatedOptions,
    paymentMethodOptions,
    showDateRange,
  ]);

  const columns: ColumnsType<InvoiceTableRow> = [
    {
      title: 'Invoice Number',
      dataIndex: 'invoice_number',
      key: 'invoice_number',
    },
    {
      title: 'Customer Name',
      dataIndex: 'partner_name',
      key: 'partner_name',
    },
    {
      title: 'Department Name',
      dataIndex: 'department_name',
      key: 'department_name',
    },
    {
      title: 'Task Type',
      dataIndex: 'referenceable_type_label',
      key: 'referenceable_type_label',
    },
    {
      title: 'Task Code',
      dataIndex: 'referenceable_code',
      key: 'referenceable_code',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
    },
    {
      title: 'Total',
      dataIndex: 'total',
      key: 'total',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Tooltip title="View">
            <Button
              type="text"
              onClick={() =>
                navigate('/view-invoice', {
                  state: {
                    invoiceId: record.id,
                    invoice_number: record.invoice_number,
                    invoice_status: record.status,
                    model: 'INV',
                    total: record.total,
                  },
                })
              }
              icon={<EyeOutlined />}
              style={{ color: '#1890ff' }}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const handleFilters = (values: { [key: string]: any }) => {
    const newFilters: { [key: string]: string | null } = { ...values };

    // Toggle date range visibility
    const hasDateRelatedTo = !!values.date_related_to;
    setShowDateRange(hasDateRelatedTo);

    // Default status
    if (!newFilters.status) {
      newFilters.status = 'PENDING';
    }

    // Handle date range
    if (hasDateRelatedTo && values.date_range?.length === 2) {
      newFilters.from = values.date_range[0] ? dayjs(values.date_range[0]).format('YYYY-MM-DD') : null;
      newFilters.to = values.date_range[1] ? dayjs(values.date_range[1]).format('YYYY-MM-DD') : null;

      delete newFilters.date_range;
    } else {
      // Clear dates if date_related_to is removed
      delete newFilters.from;
      delete newFilters.to;
      delete newFilters.date_range;
    }

    setFilters(newFilters);
  };

  return (
    <BaseCard id="basic-table" title="Invoices" padding="1.25rem">
      {initialFiltersLoaded && (
        <FilterComponent data={searchData} onFilter={handleFilters} defaultValues={{ status: 'PENDING' }} />
      )}
      <BaseTable
        columns={columns}
        dataSource={tableData.data}
        pagination={{
          current: tableData.pagination.current,
          pageSize: tableData.pagination.pageSize,
          total: tableData.pagination.total,
          showTotal,
        }}
        loading={tableData.loading}
        onChange={handleTableChange}
        scroll={{ x: true, scrollToFirstRowOnChange: true }}
      />
    </BaseCard>
  );
};
